import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

import { Button } from "@blueskydataplatform/shared-ui/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@blueskydataplatform/shared-ui/components/ui/card";
import { Input } from "@blueskydataplatform/shared-ui/components/ui/input";
import { Alert, AlertDescription, AlertTitle } from "@blueskydataplatform/shared-ui/components/ui/alert";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@blueskydataplatform/shared-ui/components/ui/form";
import { UILink } from "@/components/ui-link";
import { paths } from "@/config/paths";
import { toast } from "@blueskydataplatform/shared-ui/hooks/use-toast";
import { useResetPassword } from "../api/reset-password";
import Logo from "@/components/logo";
import useQueryParams from "@/hooks/use-query-params";
import { useNavigate } from "react-router";
import { CheckCircle2Icon } from "lucide-react";
const schema = z.object({
  email: z.string().email({ message: "Invalid email" }),
  emailCode: z.string().min(1, { message: "Enter the code" }),
  password: z.string().min(8, { message: "At least eight characters" }),
});

type FormValues = z.infer<typeof schema>;

const PasswordResetCode = ({ email, emailCode }: { email: string; emailCode: string }) => {
  const { email: emailQuery, emailCode: emailCodeQuery } = useQueryParams();
  const navigate = useNavigate();
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      email: emailQuery || email || "",
      emailCode: emailCodeQuery || emailCode || "",
      password: "",
    },
  });

  const resetPasswordMutation = useResetPassword({
    mutationConfig: {
      onSuccess: () => {
        toast.success("Password reset successfully.");
        navigate(paths.home.getHref());
      },
      onError: (error: any) => {
        if (error.response.status === 400) {
          const data = error.response.data;
          if (data.code === "INVALID_EMAIL_CODE") {
            toast.error("The code is invalid or has expired.");
            form.setError("emailCode", { message: "The code is invalid or has expired." });
            form.setFocus("emailCode");
            return;
          }
          if (data.code === "INVALID_PASSWORD") {
            toast.error("The password is too common and not valid.");
            form.setError("password", { message: "The password is too common and not valid." });
            form.setFocus("password");
            return;
          }
          if (data.code === "TOO_MANY") {
            toast.error("We've sent too many requests to this email address. Please try again later.");
            return;
          }
        }
        toast.error("Failed to reset password, please try again later.");
      },
    },
  });

  const onSubmit = (values: FormValues) => {
    resetPasswordMutation.mutate({ data: { email: values.email, code: values.emailCode, password: values.password } });
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <Card className="w-full max-w-xl">
        <CardHeader className="space-y-1">
          <div className="flex justify-center mb-2">
            <Logo className="mb-4" />
          </div>
          <CardTitle className="text-2xl font-bold text-center">Reset your password</CardTitle>
          <CardDescription className="text-left">
            Enter the email address associated with your account, the verification code and the new password for your
            account.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!emailCode && !emailCodeQuery && (
            <Alert variant="default" className="mb-4">
              <CheckCircle2Icon />
              <AlertTitle>Verification code sent</AlertTitle>
              <AlertDescription>
                We&apos;ve sent a verification code to your email address. Please check your inbox and enter the code
                below.
              </AlertDescription>
            </Alert>
          )}
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input type="email" disabled placeholder="Enter your email" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="emailCode"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Code</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter code" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>New Password</FormLabel>
                    <FormControl>
                      <Input type="password" placeholder="New Password" {...field} />
                    </FormControl>
                    <p className="text-xs text-gray-500">Must have at least eight characters</p>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button type="submit" className="w-full" disabled={resetPasswordMutation.isPending}>
                {resetPasswordMutation.isPending ? "Resetting password..." : "Reset password"}
              </Button>

              <div className="text-center text-sm">
                <UILink to={paths.home.getHref()} className="text-sm">
                  Return to sign in
                </UILink>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
};

export default PasswordResetCode;
